(() => {
  'use strict';
  const pendingKey = 'mr-tour-guide-pending-v1';
  const accessKey = 'mr-tour-guide-access-v1';
  const pendingLifetime = 15 * 60 * 1000;

  function readPending() {
    try { return JSON.parse(sessionStorage.getItem(pendingKey) || 'null'); }
    catch (_) { return null; }
  }

  function clearPending(rootId) {
    try { if (readPending()?.id === rootId) sessionStorage.removeItem(pendingKey); }
    catch (_) {}
  }

  function hasAccess() {
    try { return !!localStorage.getItem(accessKey); }
    catch (_) { return false; }
  }

  function grantAccess(source) {
    try {
      localStorage.setItem(accessKey, JSON.stringify({ grantedAt: Date.now(), source: source || 'guide-signup' }));
    } catch (_) {}
    document.dispatchEvent(new CustomEvent('mr:guide-access'));
  }

  function maybeGrantFromUrl() {
    try {
      const params = new URLSearchParams(location.search);
      if (params.get('guides') === 'unlocked') grantAccess('guide-link');
    } catch (_) {}
  }

  function showUnlocked(root) {
    const form = root.querySelector('.mrtg-form');
    const unlocked = root.querySelector('[data-mrtg-unlocked]');
    if (!unlocked) return;
    if (form) form.hidden = true;
    unlocked.hidden = false;
  }

  function setup(root) {
    if (root.dataset.mrtgInitialized === 'true') return;
    const form = root.querySelector('.mrtg-form');
    const details = root.querySelector('[data-mrtg-details]');
    const email = root.querySelector('[data-mrtg-email]');
    const ready = root.querySelector('[data-mrtg-ready]');
    const unlocked = root.querySelector('[data-mrtg-unlocked]');
    const error = root.querySelector('[data-mrtg-error]');
    if (!form || !details || !email || !unlocked) return;
    root.dataset.mrtgInitialized = 'true';

    const summary = details.querySelector('summary');
    const pending = readPending();
    const hash = location.hash.slice(1);
    const ownHash = hash === root.id || hash === form.id;
    const ownPending = pending?.id === root.id && pending.path === location.pathname && Date.now() - pending.time < pendingLifetime;
    const hashTarget = hash ? document.getElementById(hash) : null;
    const targetForm = hashTarget?.matches('form') ? hashTarget : hashTarget?.closest('form') || hashTarget?.querySelector('form');
    const anotherCustomerForm = !ownHash && !ownPending && targetForm && targetForm !== form && targetForm.querySelector('input[name="form_type"]')?.value === 'customer';
    let userToggle = false;

    function resetForm() {
      form.reset();
      email.value = '';
      email.removeAttribute('aria-invalid');
      if (error) {
        error.hidden = true;
        const descriptions = (email.getAttribute('aria-describedby') || '').split(/\s+/).filter(id => id && id !== error.id);
        email.setAttribute('aria-describedby', descriptions.join(' '));
      }
    }

    // A successful guide request unlocks the entire library in this browser.
    // Shopify doesn't always render posted_successfully? for an email address that
    // is already subscribed. A fresh pending submission returning to this exact
    // guide with no form error is therefore also treated as a completed request.
    const completedExistingSubscriber = ownPending && !error && !anotherCustomerForm;
    if ((ready || completedExistingSubscriber) && !anotherCustomerForm) {
      grantAccess(root.dataset.mrtgKey || 'guide-signup');
      clearPending(root.id);
      if (ready) {
        requestAnimationFrame(() => ready.focus({ preventScroll: true }));
      } else {
        showUnlocked(root);
        return;
      }
    } else if (hasAccess()) {
      showUnlocked(root);
      clearPending(root.id);
      return;
    } else if (ready && anotherCustomerForm) {
      ready.hidden = true;
      details.hidden = false;
      details.open = false;
    } else if (error) {
      if (!anotherCustomerForm) {
        clearPending(root.id);
        details.open = true;
        requestAnimationFrame(() => error.focus({ preventScroll: true }));
      } else {
        details.open = false;
        resetForm();
      }
    }

    summary?.addEventListener('click', () => { userToggle = true; });
    details.addEventListener('toggle', () => {
      if (!userToggle) return;
      userToggle = false;
      if (details.open) email.focus({ preventScroll: true });
      else resetForm();
    });
    details.addEventListener('keydown', event => {
      if (event.key !== 'Escape' || !details.open) return;
      event.preventDefault();
      event.stopPropagation();
      details.open = false;
      resetForm();
      summary?.focus({ preventScroll: true });
    });
    email.addEventListener('input', () => email.removeAttribute('aria-invalid'));
    form.addEventListener('submit', () => {
      try { sessionStorage.setItem(pendingKey, JSON.stringify({ id: root.id, path: location.pathname, time: Date.now() })); }
      catch (_) {}
    });
  }

  function setupLibrary(library) {
    if (library.dataset.mrtgLibraryInitialized === 'true') return;
    library.dataset.mrtgLibraryInitialized = 'true';
    const locked = library.querySelector('[data-mrtg-library-locked]');
    const unlocked = library.querySelector('[data-mrtg-library-unlocked]');
    const sync = () => {
      const access = hasAccess();
      if (locked) locked.hidden = access;
      if (unlocked) unlocked.hidden = !access;
    };
    sync();
    document.addEventListener('mr:guide-access', sync);
    window.addEventListener('storage', event => { if (event.key === accessKey) sync(); });
  }

  function initialize(scope) {
    if (scope.matches?.('[data-mrtg]')) setup(scope);
    scope.querySelectorAll?.('[data-mrtg]').forEach(setup);
    if (scope.matches?.('[data-mrtg-library]')) setupLibrary(scope);
    scope.querySelectorAll?.('[data-mrtg-library]').forEach(setupLibrary);
  }

  maybeGrantFromUrl();
  initialize(document);
  document.addEventListener('shopify:section:load', event => initialize(event.target));
  document.addEventListener('mr:guide-access', () => document.querySelectorAll('[data-mrtg]').forEach(root => {
    if (!root.querySelector('[data-mrtg-ready]')) showUnlocked(root);
  }));
})();
